import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { EvPriceRateRow } from '../../../../core/models/system-settings/ev-price-rate.model';
import { MapperEvPriceRateService } from '../../../../core/mappers/system-settings/mapper-ev-price-rate.service';
import { ApiEvPriceRateService } from '../../../../core/services/api/system-settings/api-ev-price-rate.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalEvPriceRateComponent } from '../../modals/system-settings/modal-ev-price-rate/modal-ev-price-rate.component';

@Component({
  selector: 'app-ev-price-rates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    PssConfirmModalComponent,
    ModalEvPriceRateComponent,
  ],
  templateUrl: './ev-price-rates.component.html',
  styleUrl: './ev-price-rates.component.css',
})
export class EvPriceRatesComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiEvPriceRateService);
  private readonly mapper = inject(MapperEvPriceRateService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  items = signal<EvPriceRateRow[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  selectedId = signal(0);
  searchText = signal('');
  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(1);

  isConfirmDeactivateOpen = signal(false);
  pendingDeactivate: EvPriceRateRow | null = null;

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.currentPage.set(1);
        this.loadTable();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(this.totalPages(), startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  get deactivateHighlight(): string {
    const item = this.pendingDeactivate;
    if (!item) return '';
    return item.name || `${item.connectorCode || 'Rate'} #${item.id}`;
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadTable();
  }

  onSearchChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Sidebar');
      return;
    }
    this.selectedId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(item: EvPriceRateRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedId.set(item.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedId.set(0);
  }

  onSaveSuccess(): void {
    this.closeModal();
    this.loadTable();
  }

  openDeactivateConfirm(item: EvPriceRateRow): void {
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ปิดใช้งาน');
      return;
    }
    if (!item.active) {
      this.toast.error('รายการนี้ปิดใช้งานแล้ว');
      return;
    }
    this.pendingDeactivate = item;
    this.isConfirmDeactivateOpen.set(true);
  }

  closeDeactivateConfirm(): void {
    this.isConfirmDeactivateOpen.set(false);
    this.pendingDeactivate = null;
  }

  confirmDeactivate(): void {
    const item = this.pendingDeactivate;
    this.closeDeactivateConfirm();
    if (!item) return;

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    this.isLoading.set(true);
    this.api
      .deactivateEvPriceRate({ userId, menuId, id: item.id })
      .subscribe({
        next: (res) => {
          this.isLoading.set(false);
          const mapped = this.mapper.mapActionResponse(res);
          if (mapped.isSuccess) {
            this.toast.success(mapped.message || 'ปิดใช้งานเรียบร้อย');
            this.loadTable();
          } else {
            this.toast.error(mapped.message || 'ปิดใช้งานไม่สำเร็จ');
          }
        },
        error: (err) => {
          this.isLoading.set(false);
          this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
        },
      });
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  trackById(_: number, item: EvPriceRateRow): number {
    return item.id;
  }

  billingLabel(mode: string): string {
    return this.mapper.billingLabel(mode);
  }

  unitDisplay(row: EvPriceRateRow): string {
    return this.mapper.unitDisplay(row);
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      return;
    }

    this.isLoading.set(true);
    this.api
      .getEvPriceRatesForTable({
        page: this.currentPage(),
        pageSize: this.pageSize(),
        siteId,
        search: this.searchText().trim() || undefined,
      })
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTableFromDto(res);
          this.items.set(mapped.items);
          this.totalItems.set(mapped.total);
          this.totalPages.set(mapped.totalPages || 1);
          this.currentPage.set(mapped.page || this.currentPage());
          this.pageSize.set(mapped.pageSize || this.pageSize());
          this.isLoading.set(false);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.items.set([]);
          this.toast.error(err?.error?.message || 'โหลดข้อมูลไม่สำเร็จ');
        },
      });
  }

  private resolveUserId(): number {
    const local = localStorage.getItem('userId');
    if (local) return Number(local);
    return Number(this.cookieService.get('user_id') || 0);
  }
}
